import { useErrorHandler } from '@app/core/hooks/useErrorHandler';
import { queryClient } from '@app/core/integrations/react-query/react-query';
import { useToast } from '@app/core/notifications';
import { Station } from '@app/core/types/stations';
import { CompositionDetailDto } from '@app/modules/compose/composition.types';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import {
  useVideoEditorDeleteCompositionsById,
  videoEditorGetCompositionsByIdQueryKey,
} from '@mi-front/api-v2';
import { useRouter } from '@mi-front/core';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

const composeDeleteTitleMessage = defineMessage({
  id: 'compose.delete.title',
  defaultMessage: 'Eliminar composición',
});

const composeDeleteMessageMessage = defineMessage({
  id: 'compose.delete.message',
  defaultMessage:
    '¿Estás seguro de que quieres eliminar la composición "{name}"? Esta acción no se puede deshacer.',
});

const composeDeleteSuccessToastMessage = defineMessage({
  id: 'compose.delete.successToast',
  defaultMessage: 'Composición eliminada',
});

const composeCancelActionMessage = defineMessage({
  id: 'common.actions.cancel',
  defaultMessage: 'Cancelar',
});

const composeDeleteActionMessage = defineMessage({
  id: 'common.actions.delete',
  defaultMessage: 'Eliminar',
});

export interface ComposeDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  composition: CompositionDetailDto;
  station: Station;
}

/**
 * Confirmation dialog to delete a composition. On success, refreshes the station list and navigates back.
 */
export function ComposeDeleteDialog({
  open,
  onClose,
  composition,
  station,
}: ComposeDeleteDialogProps) {
  const { $t } = useIntl();
  const { history } = useRouter();
  const { notifySuccess } = useToast();
  const { onApiError } = useErrorHandler();
  const listId = `compose-compositions-list-${station.slug}`;

  const deleteMutation = useVideoEditorDeleteCompositionsById();

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ id: composition.id });

      await queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === listId,
      });

      queryClient.removeQueries({
        queryKey: videoEditorGetCompositionsByIdQueryKey(composition.id),
      });

      notifySuccess($t(composeDeleteSuccessToastMessage));
      history.push(composeRouteGenerator.listForStationSlug(station.slug));
    } catch (error) {
      onApiError(error);
    }
  };

  return (
    <Dialog open={open} onClose={() => !deleteMutation.isPending && onClose()}>
      <DialogTitle>{$t(composeDeleteTitleMessage)}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {$t(composeDeleteMessageMessage, { name: composition.name })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleteMutation.isPending}>
          {$t(composeCancelActionMessage)}
        </Button>
        <Button
          onClick={handleDelete}
          loading={deleteMutation.isPending}
          color="error"
          variant="contained"
        >
          {$t(composeDeleteActionMessage)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
